// Ordinal statistics for the 1–5 rubric, shared by BOTH discs so that
// "typical level" means exactly one thing across the app.
//
// Rubric levels are ORDINAL: ranked categories, not quantities. Level 4 is
// "better than" level 2, but it is not "twice" level 2, and the gap 2→3 need
// not equal the gap 4→5. That rules out the mean — averaging 2 and 4 to 3
// asserts an even spacing the rubric never promised, and one outlier drags a
// summary that is supposed to describe the middle. So every roll-up drawn on a
// disc is a MEDIAN. (Means still appear as tooltip TEXT, e.g. "class average
// 3.2 / 5", where a reader can see it is a mean and judge it accordingly.)
//
// A median over an even number of grades has no single middle — there are two.
// We take the midpoint and round DOWN. Rounding down is the load-bearing
// choice: on an exact tie a bar never rounds toward the stronger claim, so a
// cohort split half-at-3 / half-at-4 reads level 3, not proficient.
// (Proficient = level 4 everywhere, ruled 2026-07-10 to match the grading
// prompt's calibration: 3 is "developing", 4 is "proficient at grade". The
// disc's emphasized ring and the mastery count in dashboard.dao follow it.)
//
// Note this is WEAKER than simply taking the lower of the two middles: where
// they are non-adjacent (twenty 2s and twenty 4s) the midpoint is 3 exactly,
// no rounding happens, and 3 is drawn — because 3 genuinely is the middle.
// Down-rounding only ever resolves a true tie between ADJACENT levels.
//
// This module is CLIENT-SAFE (no server imports): the student disc computes its
// bar medians in the renderer, the class disc computes its in the adapter, and
// both must resolve a tie the same way or the same evidence would show two
// different typical levels on two different pages.

// "Developed" threshold for all disc/growth counting: a specific skill counts
// as developed at score >= DEV_MIN. Re-ruled 2026-07-31 (was 4): aligns with
// the legacy ArcMosaico platform (shared.jsx MIN_DEVELOPED_SCORE = 3) and the
// pedagogy vocabulary (developing < 3 <= developed). NOTE: "proficient" is a
// DIFFERENT concept and stays anchored at level 4 (rubric; the emphasized
// ring-4 line in LevelRingsDisc) — do not conflate the two.
export const DEV_MIN = 3;

/** Level → number of observations at that level. Keys may be 1..5 or '1'..'5'. */
export type LevelDist = Record<string | number, number>;

/** A bar's summary: the cap sits at `med`; the whisker spans `q1`..`q3`. */
export interface LevelStats {
  med: number;
  q1: number;
  q3: number;
  n: number;
}

/** The one place the even-count tie rule lives. See the header. */
const midpoint = (lo: number, hi: number): number => Math.floor((lo + hi) / 2);

/**
 * Median of a sample of levels. Returns 0 for an empty sample — the discs read
 * 0 as "not yet assessed" and draw a dashed ghost stub rather than a value.
 */
export function ordinalMedian(levels: number[]): number {
  const n = levels.length;
  if (!n) return 0;
  const s = [...levels].sort((a, b) => a - b);
  // Odd n collapses into the even case: both indices land on the same element,
  // and midpoint(x, x) === x. No separate branch to keep in sync.
  const lo = s[Math.floor((n - 1) / 2)] ?? 0;
  const hi = s[Math.floor(n / 2)] ?? 0;
  return midpoint(lo, hi);
}

/**
 * Median + inter-quartile range of a level DISTRIBUTION, without materializing
 * the sample: rank into the cumulative counts instead.
 *
 * Quartiles stay nearest-rank (no midpoint, no rounding). A q1 of 2.5 has no
 * ring to sit on, and the whisker exists to name levels somebody actually
 * reached. `q1 <= med <= q3` holds for every distribution.
 *
 * What ONE observation means is the caller's business, and the two discs differ:
 * on a specific skill it is one student, so the whisker is a spread of students;
 * on a skill it is one student on one specific skill, so a wide whisker may mean
 * the cohort is split OR that the specific skills differ in rigor.
 */
export function medianQuartiles(dist: LevelDist | null | undefined): LevelStats {
  const counts = [0, 0, 0, 0, 0, 0];
  let n = 0;
  for (let lv = 1; lv <= 5; lv++) {
    const c = Number(dist?.[lv] ?? dist?.[String(lv)] ?? 0) || 0;
    counts[lv] = c;
    n += c;
  }
  if (!n) return { med: 0, q1: 0, q3: 0, n: 0 };

  // The level at 1-based rank `r` of the implied ascending sample.
  const at = (r: number): number => {
    let cum = 0;
    for (let lv = 1; lv <= 5; lv++) {
      cum += counts[lv] ?? 0;
      if (cum >= r) return lv;
    }
    return 5;
  };

  return {
    med: n % 2 ? at(Math.ceil(n / 2)) : midpoint(at(n / 2), at(n / 2 + 1)),
    q1: at(Math.ceil(n * 0.25)),
    q3: at(Math.ceil(n * 0.75)),
    n,
  };
}
